import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "431",
  "slug": "what-is-spatial-resolution-satellite-imagery",
  "title": "What Is Spatial Resolution in Satellite Imagery?",
  "excerpt": "Spatial resolution is the reason one satellite can spot a car while another sees only a city block. Here is what it means and why higher is not always better.",
  "date": "2026-06-13",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1510330324285-abbdabff83af?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjBmYXJtbGFuZCUyMGZpZWxkc3xlbnwxfDB8fHwxNzgxMzM4NTUyfDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Spatial resolution is the size of the smallest feature a satellite image can show, often described as how many metres of ground each pixel covers. Sentinel-2 sees about 10 metres per pixel, Landsat about 30, and the sharpest commercial satellites around 30 centimetres.",
  "content": [
    {
      "type": "paragraph",
      "text": "When people compare satellite images, the word that comes up most is resolution. It is the difference between a picture where you can pick out individual trees and one where a whole forest is a single green smudge. But resolution is not one thing, and the kind that matters most for what you can actually see has a specific name: spatial resolution."
    },
    {
      "type": "heading",
      "text": "A Plain Definition"
    },
    {
      "type": "paragraph",
      "text": "Spatial resolution describes the size of the smallest object a satellite image can clearly show. It is usually expressed as the amount of ground covered by a single pixel, a figure often called the ground sample distance. A 10-metre resolution means each pixel represents a 10 by 10 metre square on the ground. The smaller that number, the finer the detail, so a 0.3-metre image is far sharper than a 30-metre one."
    },
    {
      "type": "paragraph",
      "text": "Think of it like the squares on graph paper laid over a landscape. Big squares blur many features into one pixel. Small squares capture fine detail. Spatial resolution is simply how big those squares are, and it sets a hard floor on what the image can ever reveal."
    },
    {
      "type": "heading",
      "text": "How Sharp Are Real Satellites?"
    },
    {
      "type": "paragraph",
      "text": "It helps to anchor the idea with the satellites that actually fly today:"
    },
    {
      "type": "list",
      "items": [
        "MODIS and similar wide-view sensors: hundreds of metres per pixel, designed to image the whole planet daily rather than show detail.",
        "Landsat: around 30 metres per pixel, the workhorse resolution behind more than fifty years of Earth observation.",
        "Sentinel-2: around 10 metres per pixel, free, frequent, and detailed enough to see fields, roads, and urban blocks.",
        "Commercial satellites such as those run by Maxar and Planet: down to roughly 30 centimetres per pixel, sharp enough to make out individual vehicles."
      ]
    },
    {
      "type": "heading",
      "text": "Why You Cannot Read a Licence Plate"
    },
    {
      "type": "paragraph",
      "text": "A common myth, fuelled by films, is that a spy satellite can read the licence plate on your car. In reality, even the sharpest commercial imagery, at around 30 centimetres per pixel, cannot resolve something as small as a few centimetres of text. The physics of optics impose a limit too: there is a diffraction barrier set by the size of the camera and the distance involved, and the shimmering atmosphere blurs fine detail further. Most everyday satellite imagery is far coarser than the sharpest available anyway, so a car is a blob, not a readable object."
    },
    {
      "type": "heading",
      "text": "Higher Resolution Is Not Always Better"
    },
    {
      "type": "paragraph",
      "text": "It is tempting to think sharper always wins, but resolution comes with trade-offs. A satellite that sees fine detail can only photograph a narrow strip at a time, so it covers less ground and revisits the same place less often. A coarse, wide-view satellite can image the entire planet every single day. Sharper images are also vastly larger files that cost more to capture, transmit, store, and process."
    },
    {
      "type": "paragraph",
      "text": "So the right resolution depends on the job. Tracking a wildfire spreading across a continent needs frequent, wide coverage, not centimetre detail. Inspecting damage to a single building needs the opposite. There is no universally best resolution, only the best one for a given question, which is why agencies fly fleets of satellites at different scales rather than one perfect camera."
    },
    {
      "type": "heading",
      "text": "The Other Kinds of Resolution"
    },
    {
      "type": "paragraph",
      "text": "Spatial resolution gets the headlines, but satellite imagery has three other dimensions worth knowing:"
    },
    {
      "type": "list",
      "items": [
        "Temporal resolution: how often a satellite revisits the same spot, from days to weeks.",
        "Spectral resolution: how many bands of light it captures, including invisible infrared used to study vegetation, water, and heat.",
        "Radiometric resolution: how finely it distinguishes subtle differences in brightness within each band."
      ]
    },
    {
      "type": "paragraph",
      "text": "A truly useful image usually balances all four rather than maximising any single one, which is part of the craft of choosing the right satellite for a task."
    },
    {
      "type": "heading",
      "text": "Why It Matters for Reading the Earth"
    },
    {
      "type": "paragraph",
      "text": "Spatial resolution explains a lot about why some satellite views are easy to identify and others are maddeningly hard. At 10 metres per pixel, a typical Sentinel-2 view, you can make out field patterns, coastlines, and the rough texture of a city, but not signs, cars, or faces. That is exactly the sweet spot that makes guessing a location from above a genuine puzzle: enough detail to reason with, not so much that it simply gives the answer away."
    }
  ]
};
